import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { useWindowSize } from "usehooks-ts";
import CardSearch from "../../components/UploadCardsPageComponents/CardSearch/CardSearch";
import {
  MultiSelectConditionType,
  CardSelected,
  MultiSelectLanguageType,
  StatsDataType,
} from "../../type";
import { ModalChooseCardDetail } from "../../components/UploadCardsPageComponents/ModalChooseCardDetail/ModalChooseCardDetail";
import { CardBoard } from "../../components/CardBoard/CardBoard";
import { ModalAddPurchasePrice } from "../../components/UploadCardsPageComponents/ModalAddPurchasePrice/ModalAddPurchasePrice";
import { ModalPricing } from "../../components/ModalPricing/ModalPricing";
import { Box, FlexBox } from "../../components/primitives";
import { SideMenuWrapper } from "./uploadCardsPage.styled";
import { LeftSideMenuUploadCards } from "../../components/UploadCardsPageComponents/LeftSideMenuUploadCards/LeftSideMenuUploadCards";
import { handleFilterAndSortCards } from "../../utils";
import { Header } from "../../components/UploadCardsPageComponents/Header/Header";
import { useCards } from "../../context/CardsContext";
import {
  DEFAULT_STATE_CARD,
  LOCAL_STORAGE_DEFAULT_CARD_STATS,
} from "../../constant";
import localforage from "localforage";
import { useSelector } from "react-redux";
import { selectGame } from "../../store/slices/global";
import { HiOutlineMenu } from "react-icons/hi";
import { PrimaryButton } from "../../components/common/common.style";
import { TestPicture } from "../TestCamera/TestPicture";

export const UploadCardsPage = () => {
  const [cardsBoard, setCardsBoard] = useState<CardSelected[]>(() => {
    const stored = localStorage.getItem("cardsBoard");
    return stored ? JSON.parse(stored) : [];
  });
  const [cardSelected, setCardsSelected] = useState<CardSelected | null>(null);
  const [editCard, setEditCard] = useState<string | null>(null);
  const [cardsClicked, setCardsClicked] = useState<string[]>([]);

  const [openModalAddPurchasePrice, setOpenModalAddPurchasePrice] =
    useState(false);
  const [openModalChoosePrice, setOpenModalChoosePrice] = useState<
    string | null
  >(null);
  const [defaultStatsValue, setDefaultStatsValue] = useState<StatsDataType>(
    () => {
      const stored = localStorage.getItem(LOCAL_STORAGE_DEFAULT_CARD_STATS);
      return stored ? JSON.parse(stored) : DEFAULT_STATE_CARD;
    }
  );
  //----------------- Sorting and filtering ---------------------------
  const [searchText, setSearchText] = useState("");
  const [searchTextComment, setSearchTextComment] = useState("");
  const [sortOption, setSortOption] = useState("date-desc");
  const [priceMin, setPriceMin] = useState<number | undefined>();
  const [priceMax, setPriceMax] = useState<number | undefined>();
  const [filterOption, setFilterOption] = useState("none");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [debouncedSearchTextComment, setDebouncedSearchTextComment] =
    useState("");
  const [languagesFilter, setLanguagesFilter] = useState<
    MultiSelectLanguageType[]
  >([]);
  const [conditionsFilter, setConditionsFilter] = useState<
    MultiSelectConditionType[]
  >([]);
  //-------------------------------------------------------------------
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCameraUploadOpen, setIsCameraUploadOpen] = useState(false);
  const foundCardComplexArray = useCards();
  const game = useSelector(selectGame);

  const { width } = useWindowSize();
  const isMobile = width <= 640;

  useEffect(() => {
    if (!isMobile) {
      setIsMenuOpen(true);
    }
  }, [isMobile]);

  const cardsBoardFiltered = useMemo(() => {
    return handleFilterAndSortCards({
      debouncedSearchText,
      cardsBoard,
      sortOption,
      foundCardComplexArray,
      priceMin,
      priceMax,
      filterOption,
      game_id: game.id,
      languagesFilter: languagesFilter.map((lang) => lang.value),
      conditionsFilter: conditionsFilter.map((con) => con.value),
      debouncedSearchTextComment,
    });
  }, [
    debouncedSearchText,
    cardsBoard,
    sortOption,
    foundCardComplexArray,
    priceMin,
    priceMax,
    filterOption,
    game,
    languagesFilter,
    conditionsFilter,
    debouncedSearchTextComment,
  ]);

  useEffect(() => {
    const uids = cardsBoard.map((card) => card.uid);

    const missingUid = cardsBoard.filter((card) => !card.uid);
    if (missingUid.length > 0) {
      console.warn(`Cards missing UID: ${missingUid.length}`, missingUid);
    }

    const uidCounts = uids.reduce((acc, uid) => {
      //@ts-ignore
      if (uid) acc[uid] = (acc[uid] || 0) + 1;
      return acc;
    }, {});

    const duplicates = Object.entries(uidCounts).filter(
      // @ts-ignore
      ([uid, count]) => count > 1
    );
    if (duplicates.length > 0) {
      console.warn("Duplicate UIDs detected:", duplicates);
    }

    setCardsClicked((prevClicked) =>
      prevClicked.filter((uid) => uids.includes(uid))
    );
  }, [cardsBoard]);

  useMemo(() => {
    if (game) {
      setDefaultStatsValue(DEFAULT_STATE_CARD);
    }
  }, [game]);

  useEffect(() => {
    const loadCards = async () => {
      try {
        const storedCards = await localforage.getItem("cardsBoard");
        if (storedCards && Array.isArray(storedCards)) {
          console.log(`Loaded ${storedCards.length} cards from LocalForage`);
          setCardsBoard(storedCards);
        }
      } catch (error) {
        console.error("Error loading cards from LocalForage:", error);
      }
    };
    loadCards();
  }, []);

  useEffect(() => {
    const saveCards = async () => {
      try {
        const uniqueCards = Array.from(
          new Map(cardsBoard.map((card) => [card.uid, card])).values()
        );
        await localforage.setItem("cardsBoard", uniqueCards);
        console.log(`Saved ${uniqueCards.length} unique cards to LocalForage`);
      } catch (error) {
        console.error("Error saving cards to LocalForage:", error);
      }
    };

    if (cardsBoard.length > 0) {
      saveCards();
    }
  }, [cardsBoard]);

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_DEFAULT_CARD_STATS,
      JSON.stringify(defaultStatsValue)
    );
  }, [defaultStatsValue]);

  useEffect(() => {
    if (editCard) {
      const cardFoundToEdit = cardsBoard.find((card) => card.uid === editCard);
      if (cardFoundToEdit) setCardsSelected(cardFoundToEdit);
    }
  }, [cardsBoard, editCard]);

  useEffect(() => {
    if (openModalChoosePrice) {
      const cardFoundToEdit = cardsBoard.find(
        (card) => card.uid === openModalChoosePrice
      );
      if (cardFoundToEdit) setCardsSelected(cardFoundToEdit);
    }
  }, [cardsBoard, openModalChoosePrice]);

  const handleSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchText(value);
      }, 300),
    []
  );

  useEffect(() => {
    handleSearchChange(searchText);
  }, [searchText, handleSearchChange]);

  const handleSearchChangeComment = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTextComment(value);
      }, 300),
    []
  );

  useEffect(() => {
    handleSearchChangeComment(searchTextComment);
  }, [searchTextComment, handleSearchChangeComment]);

  const renderRightModal = () => {
    if (cardSelected && openModalChoosePrice) {
      return (
        <ModalPricing
          setOpenModalChoosePrice={setOpenModalChoosePrice}
          cardSelected={cardSelected}
          setCardsSelected={setCardsSelected}
          setCardsBoard={setCardsBoard}
          openModalChoosePrice={openModalChoosePrice}
          cardsBoard={cardsBoard}
        />
      );
    }

    if (cardSelected) {
      return (
        <ModalChooseCardDetail
          setCardsSelected={setCardsSelected}
          cardsBoard={cardsBoard}
          setCardsBoard={setCardsBoard}
          cardSelected={cardSelected}
          editCard={editCard}
          setEditCard={setEditCard}
        />
      );
    }
  };

  return (
    <>
      {isCameraUploadOpen && (
        <TestPicture
          setIsCameraUploadOpen={setIsCameraUploadOpen}
          defaultStatsValue={defaultStatsValue}
          setCardsBoard={setCardsBoard}
        />
      )}
      {renderRightModal()}
      {openModalAddPurchasePrice && (
        <ModalAddPurchasePrice
          setOpenModalAddPurchasePrice={setOpenModalAddPurchasePrice}
          cardsBoard={cardsBoard}
          setCardsBoard={setCardsBoard}
        />
      )}
      <Box css={{ backgroundColor: "#f0fdf4" }}>
        <Box
          css={{
            position: "fixed",
            borderRadius: "4px",
            padding: "4px 6px",
            backgroundColor: "#fff",
            cursor: "pointer",
            background: "$primary",
            zIndex: 10001,
          }}
          hiddenUp="sm"
          onClick={() => {
            setIsMenuOpen(!isMenuOpen);
          }}
        >
          <HiOutlineMenu color="white" size={18} />
        </Box>
        <FlexBox>
          {isMenuOpen && (
            <SideMenuWrapper>
              <FlexBox
                hiddenUp="sm"
                css={{ padding: "20px", justifyContent: "center" }}
              >
                <PrimaryButton
                  isBig
                  css={{ width: 200 }}
                  onClick={() => {
                    setIsMenuOpen(false);
                    setIsCameraUploadOpen(true);
                  }}
                >
                  UPLOAD CARDS WITH CAMERA
                </PrimaryButton>
              </FlexBox>
              <Box>
                <CardSearch
                  defaultStatsValue={defaultStatsValue}
                  setCardsSelected={setCardsSelected}
                  cardSelected={cardSelected}
                />
              </Box>
              <LeftSideMenuUploadCards
                setDefaultStatsValue={setDefaultStatsValue}
                defaultStatsValue={defaultStatsValue}
                setSortOption={setSortOption}
                sortOption={sortOption}
                searchText={searchText}
                setSearchText={setSearchText}
                priceMin={priceMin}
                priceMax={priceMax}
                setPriceMin={setPriceMin}
                setPriceMax={setPriceMax}
                filterOption={filterOption}
                setFilterOption={setFilterOption}
                cardsBoard={cardsBoardFiltered}
                setLanguagesFilter={setLanguagesFilter}
                setConditionsFilter={setConditionsFilter}
                languagesFilter={languagesFilter}
                conditionsFilter={conditionsFilter}
                searchTextComment={searchTextComment}
                setSearchTextComment={setSearchTextComment}
                hasFailedCards={
                  !!cardsBoardFiltered?.filter((card) => card.hasPriceIssue)
                    .length
                }
              />
            </SideMenuWrapper>
          )}
          <Box css={{ width: "100%" }}>
            <Header
              isCardsBoardEmpty={
                !cardsBoardFiltered?.filter((card) =>
                  cardsClicked.includes(card.uid)
                ).length
              }
              setModalAddPurchasePrice={setOpenModalAddPurchasePrice}
              setCardsBoard={setCardsBoard}
              cardsBoard={cardsBoard}
              cardsBoardCanDoOpeation={cardsBoardFiltered?.filter((card) =>
                cardsClicked.includes(card.uid)
              )}
              cardsBoardFiltered={cardsBoardFiltered}
              cardsClicked={cardsClicked}
              setCardsClicked={setCardsClicked}
            />
            <CardBoard
              cardsBoardFiltered={cardsBoardFiltered}
              cardsBoard={cardsBoard}
              setCardsBoard={setCardsBoard}
              setEditCard={setEditCard}
              setOpenModalChoosePrice={setOpenModalChoosePrice}
              setCardsClicked={setCardsClicked}
              cardsClicked={cardsClicked}
            />
          </Box>
        </FlexBox>
      </Box>
    </>
  );
};
